// Topbar interactivity:
//   - Profile avatar opens a dropdown on hover (with intent delay) or click
//   - Cmd+K (⌘K / Ctrl+K) opens/focuses the global search
//
// The search bar itself is rendered with `hidden` and stays visually hidden
// for now — the markup and shortcut are wired so we can flip a single flag
// when the search backend is ready.

const HOVER_OPEN_DELAY  = 80;   // ms — guard against accidental hovers
const HOVER_CLOSE_DELAY = 180;  // ms — gives users time to enter the menu

const initProfileMenu = (root) => {
  const profile = root.querySelector("[data-topbar-profile]");
  if (!profile) return;

  const trigger = profile.querySelector("[data-topbar-profile-trigger]");
  const menu = profile.querySelector("[data-topbar-profile-menu]");
  if (!trigger || !menu) return;

  let openTimer = null;
  let closeTimer = null;

  const open = () => {
    clearTimeout(closeTimer);
    if (profile.classList.contains("is-open")) return;
    profile.classList.add("is-open");
    trigger.setAttribute("aria-expanded", "true");
  };

  const close = () => {
    clearTimeout(openTimer);
    if (!profile.classList.contains("is-open")) return;
    profile.classList.remove("is-open");
    trigger.setAttribute("aria-expanded", "false");
  };

  const scheduleOpen = () => {
    clearTimeout(closeTimer);
    openTimer = setTimeout(open, HOVER_OPEN_DELAY);
  };

  const scheduleClose = () => {
    clearTimeout(openTimer);
    closeTimer = setTimeout(close, HOVER_CLOSE_DELAY);
  };

  profile.addEventListener("mouseenter", scheduleOpen);
  profile.addEventListener("mouseleave", scheduleClose);
  menu.addEventListener("mouseenter", () => clearTimeout(closeTimer));
  menu.addEventListener("mouseleave", scheduleClose);

  trigger.addEventListener("click", (event) => {
    event.preventDefault();
    profile.classList.contains("is-open") ? close() : open();
  });

  trigger.addEventListener("focus", open);

  document.addEventListener("click", (event) => {
    if (!profile.contains(event.target)) close();
  });

  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape") close();
  });

  // Close after navigating via a menu link (Turbo replaces the body, but if
  // the link lands on the same page we still want the menu collapsed).
  menu.querySelectorAll("a").forEach((link) => link.addEventListener("click", close));
};

const initSearchShortcut = (root) => {
  const trigger = root.querySelector("[data-topbar-search-trigger]");
  if (!trigger) return;

  const openPalette = (query = "") => {
    document.dispatchEvent(new CustomEvent("cmdk:open", { detail: { query } }));
  };

  trigger.addEventListener("click", () => openPalette());
  // ⌘K is also handled by the palette itself; this is a no-op safety wire so
  // focus returns to the topbar trigger if the palette is missing.
};

const initTopbar = () => {
  const topbar = document.getElementById("app-topbar");
  if (!topbar || topbar.dataset.initialized === "true") return;
  topbar.dataset.initialized = "true";

  initProfileMenu(topbar);
  initSearchShortcut(topbar);
};

export { initTopbar };
